
export interface CheckInQuestion {
  type: 'multiple-choice' | 'text' | 'mood';
  question: string;
  options?: string[];
}

export interface Lesson {
  day: number;
  title: string;
  subtitle: string;
  videoId: string;
  content: string[];
  actionStep: {
    title: string;
    description: string;
  };
  checkInQuestion: CheckInQuestion;
  quote?: string;
  quoteAuthor?: string;
  points: number;
}

export const lessons: Lesson[] = [
  {
    day: 1,
    title: "Understanding Your Hormonal Cycle",
    subtitle: "The Basics of Female Hormonal Health",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your monthly cycle is governed by four key hormones: estrogen, progesterone, follicle-stimulating hormone (FSH), and luteinizing hormone (LH).",
      "These hormones fluctuate throughout your cycle, affecting everything from your energy levels to your mood, appetite, and even skin condition.",
      "Understanding these patterns helps you harness the unique strengths of each phase and better manage challenging symptoms."
    ],
    actionStep: {
      title: "Track Your Cycle",
      description: "Start noting the first day of your period and any symptoms you experience throughout the month. This baseline awareness is your first step to hormonal harmony."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "How regular is your menstrual cycle?",
      options: [
        "Very regular (28-30 days consistently)",
        "Somewhat regular (varies by a few days)",
        "Irregular (varies by more than 7 days)",
        "Extremely irregular/unpredictable",
        "I don't currently menstruate"
      ]
    },
    quote: "The better you understand your body, the better you can care for it.",
    points: 10
  },
  {
    day: 2,
    title: "Estrogen: Your Power Hormone",
    subtitle: "Understanding Estrogen's Role in Your Body",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Estrogen is often called the 'power hormone' because it influences everything from brain function to bone health.",
      "During the first half of your cycle (follicular phase), rising estrogen can boost your confidence, verbal skills, and creative thinking.",
      "Low estrogen can lead to fatigue, mood changes, and decreased libido, while balanced levels support energy and cognitive function."
    ],
    actionStep: {
      title: "Estrogen-Supporting Nutrition",
      description: "Include phytoestrogen-rich foods like flaxseeds, chickpeas, and fermented soy to naturally support healthy estrogen levels."
    },
    checkInQuestion: {
      type: "mood",
      question: "How are your energy levels today?",
    },
    points: 10
  },
  {
    day: 3,
    title: "Progesterone: Your Calming Hormone",
    subtitle: "Finding Balance with Progesterone",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Progesterone rises after ovulation, bringing a sense of calm and improving sleep quality.",
      "This hormone helps regulate anxiety, supports thyroid function, and acts as a natural diuretic.",
      "Low progesterone relative to estrogen can lead to PMS, anxiety, and sleep disturbances."
    ],
    actionStep: {
      title: "Stress Management",
      description: "Practice deep breathing for 5 minutes today. Chronic stress depletes progesterone, so regular stress management supports healthy levels."
    },
    checkInQuestion: {
      type: "text",
      question: "What helps you feel most calm when you're stressed?",
    },
    quote: "Rest when you need to. The work will wait.",
    quoteAuthor: "Hormony Wisdom",
    points: 10
  },
  {
    day: 4,
    title: "The Follicular Phase: Your Time to Shine",
    subtitle: "Harnessing Your High-Energy Days",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "The follicular phase begins with your period and ends with ovulation, typically lasting 14 days in a 28-day cycle.",
      "Rising estrogen during this phase increases energy, optimism, and cognitive sharpness.",
      "This is your biological prime time for starting new projects, making decisions, and social connections."
    ],
    actionStep: {
      title: "Energy Alignment",
      description: "Schedule one important task or meeting during your follicular phase when your natural energy and confidence are higher."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Which activities do you find most energizing?",
      options: [
        "Social gatherings with friends",
        "Creative projects or hobbies",
        "Physical exercise or movement",
        "Learning or intellectual pursuits",
        "Rest and rejuvenation activities"
      ]
    },
    points: 10
  },
  {
    day: 5,
    title: "The Luteal Phase: Your Inner Wisdom",
    subtitle: "Embracing Your Reflective Time",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "The luteal phase occurs after ovulation as progesterone rises, often bringing a more introspective energy.",
      "This phase enhances your attention to detail and ability to evaluate what's working and what isn't in your life.",
      "Working with this energy rather than against it can transform PMS from a burden to a time of powerful insight."
    ],
    actionStep: {
      title: "Intuitive Check-In",
      description: "Take 10 minutes to journal about any insights or feelings that have been recurring lately. Your luteal phase often heightens awareness of what needs attention."
    },
    checkInQuestion: {
      type: "text",
      question: "What does your body need most during the luteal phase (week before your period)?",
    },
    points: 10
  },
  {
    day: 6,
    title: "Hormone-Supporting Sleep Habits",
    subtitle: "The Critical Connection Between Sleep and Hormones",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Quality sleep is foundational for hormone production and balance, particularly for melatonin, growth hormone, and cortisol.",
      "Poor sleep disrupts insulin sensitivity, hunger hormones, and reproductive hormones, creating a cascade of imbalances.",
      "Your sleep needs may change throughout your cycle, with many women needing more rest during the luteal phase."
    ],
    actionStep: {
      title: "Create a Sleep Sanctuary",
      description: "Remove electronic devices from your bedroom tonight. The blue light from screens suppresses melatonin production, which can disrupt your entire hormonal orchestra."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "How would you rate your sleep quality over the past week?",
      options: [
        "Excellent - I sleep deeply and wake refreshed",
        "Good - I generally sleep well with occasional disruptions",
        "Fair - I have trouble falling or staying asleep",
        "Poor - I regularly struggle with sleep",
        "Very poor - My sleep is severely disrupted"
      ]
    },
    quote: "Sleep is the golden chain that ties health and our bodies together.",
    quoteAuthor: "Thomas Dekker",
    points: 10
  },
  {
    day: 7,
    title: "Blood Sugar Balance: The Foundation of Hormone Health",
    subtitle: "Understanding the Insulin-Hormone Connection",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Blood sugar imbalances are one of the most common root causes of hormone disruption, affecting everything from cortisol to sex hormones.",
      "Insulin resistance can lead to excess testosterone in women, contributing to PCOS and other hormonal conditions.",
      "Stable blood sugar levels support consistent energy, better mood, reduced PMS, and improved fertility."
    ],
    actionStep: {
      title: "Protein-Rich Breakfast",
      description: "Start your day with a breakfast that includes at least 20g of protein, healthy fats, and fiber to stabilize blood sugar from the morning onward."
    },
    checkInQuestion: {
      type: "mood",
      question: "How stable has your energy been throughout the day?",
    },
    points: 15
  },
  {
    day: 8,
    title: "Thyroid Health: Your Metabolic Master",
    subtitle: "Understanding the Thyroid's Role in Hormone Balance",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your thyroid produces hormones that regulate metabolism, energy, body temperature, and even menstrual cycles.",
      "Common symptoms of thyroid imbalance include fatigue, weight changes, hair loss, irregular periods, and mood disturbances.",
      "Supporting your thyroid requires attention to nutrients like iodine, selenium, zinc, and reducing exposure to certain environmental toxins."
    ],
    actionStep: {
      title: "Thyroid-Supporting Nutrients",
      description: "Include one serving of sea vegetables like dulse or kelp this week for natural iodine, or add Brazil nuts for selenium - both crucial for thyroid hormone production."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Have you ever been tested for thyroid function?",
      options: [
        "Yes, and my results were normal",
        "Yes, and I was diagnosed with hypothyroidism",
        "Yes, and I was diagnosed with hyperthyroidism",
        "No, I've never been tested",
        "I'm not sure"
      ]
    },
    points: 10
  },
  {
    day: 9,
    title: "Cortisol: Managing Your Stress Response",
    subtitle: "How Chronic Stress Impacts Your Hormones",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Cortisol is your primary stress hormone, designed to help you respond to immediate threats but problematic when chronically elevated.",
      "High cortisol can 'steal' from other hormone pathways, particularly progesterone, creating a state of 'pregnenolone steal'.",
      "Stress management is not a luxury for hormone health—it's a necessity that impacts your entire endocrine system."
    ],
    actionStep: {
      title: "Mindful Transition Moments",
      description: "Create intentional transitions between activities today. Take 3 deep breaths before moving from one task to another to reset your nervous system."
    },
    checkInQuestion: {
      type: "text",
      question: "What is your biggest source of stress right now and how does it affect your body?",
    },
    quote: "The greatest weapon against stress is our ability to choose one thought over another.",
    quoteAuthor: "William James",
    points: 10
  },
  {
    day: 10,
    title: "Nutrition and Stress - Foods that Can Lower Cortisol",
    subtitle: "The Link Between Blood Sugar, Cortisol, and Stress",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "What you eat plays a huge role in managing cortisol. Blood sugar spikes lead to cortisol surges, which is why eating foods that stabilize your blood sugar is crucial.",
      "Eating a balanced meal with protein, healthy fats, and fiber throughout the day can prevent cortisol spikes and support your body's ability to manage stress.",
      "Anti-inflammatory foods like leafy greens, fatty fish (salmon, mackerel), and nuts also help lower inflammation and cortisol levels."
    ],
    actionStep: {
      title: "Build a Balanced Meal",
      description: "For lunch today, create a balanced meal with protein, healthy fats, and fiber. For example, try a salad with grilled chicken, avocado, mixed greens, and a sprinkle of seeds or nuts. Aim to feel satisfied, not overfull."
    },
    checkInQuestion: {
      type: "mood",
      question: "How do you feel after eating balanced meals today?",
    },
    quote: "Let food be thy medicine and medicine be thy food.",
    quoteAuthor: "Hippocrates",
    points: 15
  },
  {
    day: 11,
    title: "Environmental Endocrine Disruptors",
    subtitle: "Protecting Your Hormones from Common Toxins",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Endocrine disrupting chemicals (EDCs) are substances that can interfere with hormone production, signaling, or metabolism.",
      "Common sources include plastics, conventional beauty products, household cleaners, and pesticides on non-organic produce.",
      "The good news is that our bodies can effectively clear many toxins when given the support they need through diet, hydration, and reducing new exposures."
    ],
    actionStep: {
      title: "Plastic-Free Food Storage",
      description: "Replace one plastic food storage container with glass or stainless steel. Plastics—especially when heated—can leach chemicals that mimic estrogen in the body."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Which area of reducing toxin exposure interests you most?",
      options: [
        "Personal care products",
        "Food storage",
        "Cleaning products",
        "Water filtration",
        "Organic food choices"
      ]
    },
    points: 10
  },
  {
    day: 12,
    title: "Gut Health and Hormone Balance",
    subtitle: "The Estrobolome and Hormone Detoxification",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your gut microbiome contains specific bacteria that help metabolize and eliminate excess estrogen—collectively called the 'estrobolome'.",
      "Constipation, dysbiosis, and intestinal permeability can all contribute to estrogen dominance by preventing proper hormone clearance.",
      "Supporting gut health is a fundamental approach to maintaining balanced hormones and reducing symptoms of excess estrogen."
    ],
    actionStep: {
      title: "Prebiotic Foods",
      description: "Include one serving of prebiotic-rich foods today such as garlic, onions, leeks, asparagus, or slightly green bananas to feed your beneficial gut bacteria."
    },
    checkInQuestion: {
      type: "text",
      question: "How would you describe your digestive health, and have you noticed any connection to your hormonal symptoms?",
    },
    points: 10
  },
  {
    day: 13,
    title: "Liver Support for Hormone Detoxification",
    subtitle: "Optimizing Your Body's Hormone Clearance System",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your liver is responsible for breaking down and eliminating used hormones through a two-phase detoxification process.",
      "When the liver is overburdened, hormones can be recirculated rather than eliminated, contributing to hormone imbalance.",
      "Supporting liver function with specific foods and lifestyle practices can significantly improve hormone-related symptoms."
    ],
    actionStep: {
      title: "Morning Lemon Water",
      description: "Start tomorrow with a cup of warm water with the juice of half a fresh lemon to gently support liver function and digestion upon waking."
    },
    checkInQuestion: {
      type: "mood",
      question: "How clear and focused does your mind feel today?",
    },
    quote: "The liver is an organ of transformation in both Eastern and Western medicine traditions.",
    points: 10
  },
  {
    day: 14,
    title: "Movement for Hormone Balance",
    subtitle: "Exercise Strategies for Your Cycle",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Different types of movement affect your hormones in different ways, with high-intensity exercise increasing cortisol and gentle movement often balancing it.",
      "Syncing your workouts with your cycle can maximize benefits—more intense exercise often feels better in the follicular phase, while gentler movement suits the luteal phase.",
      "Consistency matters more than intensity—regular, enjoyable movement that you can sustain has the greatest positive impact on hormone health."
    ],
    actionStep: {
      title: "Movement Inventory",
      description: "Reflect on your current movement practices and how they make you feel. Plan one form of movement for tomorrow that feels truly supportive for where you are in your cycle."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "What type of movement feels most supportive to you right now?",
      options: [
        "Strength training/weights",
        "High-intensity cardio",
        "Yoga or stretching",
        "Walking or hiking",
        "Dancing or playful movement"
      ]
    },
    points: 15
  },
  {
    day: 15,
    title: "Cycle Syncing: Aligning with Your Hormonal Wisdom",
    subtitle: "Working with Your Body's Natural Rhythms",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Cycle syncing involves adjusting your diet, exercise, work focus, and self-care to align with the four phases of your menstrual cycle.",
      "This practice honors the natural ebbs and flows of your energy, creativity, and social needs throughout the month.",
      "Rather than forcing the same productivity every day, cycle syncing helps you leverage your hormonal strengths in each phase."
    ],
    actionStep: {
      title: "Phase Awareness",
      description: "Identify which phase of your cycle you're in today (menstrual, follicular, ovulatory, or luteal) and choose one supportive action specifically for that phase."
    },
    checkInQuestion: {
      type: "text",
      question: "Which phase of your cycle do you typically find most challenging, and why?",
    },
    quote: "The menstrual cycle is the rhythm keeper of a woman's life.",
    quoteAuthor: "Alexandra Pope",
    points: 15
  },
  {
    day: 16,
    title: "Period Power: Menstrual Phase Optimization",
    subtitle: "Making the Most of Your Menstrual Phase",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "The menstrual phase is a time of heightened intuition and inner wisdom when your body naturally encourages rest and reflection.",
      "Honoring this time with adequate rest, warmth, and nourishment sets the foundation for the rest of your cycle.",
      "Your period provides valuable feedback about your hormonal health—its length, flow, color, and associated symptoms all offer insights."
    ],
    actionStep: {
      title: "Period Self-Care Plan",
      description: "Create a simple self-care plan for your next period that includes one nourishing practice for your body, mind, and spirit."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "What is your biggest challenge during your period?",
      options: [
        "Physical discomfort/pain",
        "Emotional sensitivity",
        "Low energy/fatigue",
        "Brain fog/concentration issues",
        "Practical management of bleeding"
      ]
    },
    points: 10
  },
  {
    day: 17,
    title: "PCOS: Understanding Polycystic Ovary Syndrome",
    subtitle: "Natural Approaches for PCOS Management",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "PCOS affects approximately 1 in 10 women of reproductive age and is characterized by elevated androgens, irregular cycles, and often insulin resistance.",
      "While conventional management often focuses on birth control pills, there are many evidence-based lifestyle approaches that address root causes.",
      "Blood sugar balance, anti-inflammatory nutrition, stress management, and specific supplements can significantly improve PCOS symptoms."
    ],
    actionStep: {
      title: "Blood Sugar Balancing Snack",
      description: "Prepare a blood sugar balancing snack that includes protein, fat, and fiber—such as apple slices with almond butter and a sprinkle of cinnamon."
    },
    checkInQuestion: {
      type: "mood",
      question: "How stable has your energy felt throughout the day?",
    },
    points: 10
  },
  {
    day: 18,
    title: "Endometriosis: Understanding and Supporting",
    subtitle: "Anti-Inflammatory Approaches for Endometriosis",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Endometriosis affects approximately 10% of women and is characterized by tissue similar to the uterine lining growing outside the uterus.",
      "Inflammation, immune dysfunction, and excess estrogen all play roles in this complex condition that often causes pain and can impact fertility.",
      "While endometriosis requires medical management, specific dietary and lifestyle approaches can help manage inflammation and symptoms."
    ],
    actionStep: {
      title: "Anti-Inflammatory Addition",
      description: "Add one powerful anti-inflammatory food to your diet today, such as turmeric (with black pepper), wild-caught fatty fish, or berries."
    },
    checkInQuestion: {
      type: "text",
      question: "What approaches have you found helpful for managing pain or inflammation in your body?",
    },
    quote: "Healing is a matter of time, but it is sometimes also a matter of opportunity.",
    quoteAuthor: "Hippocrates",
    points: 10
  },
  {
    day: 19,
    title: "Perimenopause: Navigating the Transition",
    subtitle: "Understanding the Perimenopause Journey",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Perimenopause is the 4-10 year transition period before menopause, typically beginning in a woman's 40s but sometimes earlier.",
      "Fluctuating estrogen levels—not just declining levels—are responsible for many perimenopausal symptoms like hot flashes, sleep disturbances, and mood changes.",
      "This transition is a normal life phase that can be supported with specific nutrition, lifestyle, and possibly supplementation strategies."
    ],
    actionStep: {
      title: "Phytoestrogen Foods",
      description: "Include one serving of phytoestrogen-rich foods like ground flaxseeds, tempeh, or chickpeas to help modulate estrogen fluctuations."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "What aspect of perimenopause concerns you most?",
      options: [
        "Physical symptoms like hot flashes",
        "Mood and emotional changes",
        "Sleep disturbances",
        "Body composition changes",
        "I'm not in perimenopause yet"
      ]
    },
    points: 10
  },
  {
    day: 20,
    title: "Hormone-Supportive Nutrition Plan",
    subtitle: "Building Your Personal Hormone Diet",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "A hormone-supportive diet focuses on blood sugar balance, anti-inflammatory foods, and adequate protein, healthy fats, and fiber.",
      "Specific nutrients like magnesium, zinc, B vitamins, and omega-3s play crucial roles in hormone production and metabolism.",
      "Rather than restrictive dieting, focus on adding nutrient-dense foods that provide your hormonal system with the building blocks it needs."
    ],
    actionStep: {
      title: "Hormone-Supporting Plate",
      description: "For dinner tonight, create a plate that is 1/2 colorful vegetables, 1/4 quality protein, and 1/4 complex carbohydrates with a serving of healthy fats."
    },
    checkInQuestion: {
      type: "mood",
      question: "How energized do you feel after eating today?",
    },
    points: 15
  },
  {
    day: 21,
    title: "Seed Cycling for Hormone Support",
    subtitle: "Using Seeds to Support Your Cycle Phases",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Seed cycling is a food-based practice that uses different seeds during specific phases of your menstrual cycle to support hormone balance.",
      "During the follicular phase (days 1-14), flax and pumpkin seeds provide lignans and zinc to support estrogen production and clearance.",
      "During the luteal phase (days 15-28), sesame and sunflower seeds provide lignans and vitamin E to support progesterone production."
    ],
    actionStep: {
      title: "Start Seed Cycling",
      description: "Identify which phase of your cycle you're in and add 1-2 tablespoons of the appropriate seeds to your meal today, either ground fresh or as a topping."
    },
    checkInQuestion: {
      type: "text",
      question: "Have you tried seed cycling before, and if so, what was your experience?",
    },
    points: 10
  },
  {
    day: 22,
    title: "Adaptogens for Hormone Balance",
    subtitle: "Herbal Support for Your Endocrine System",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Adaptogens are herbs that help the body adapt to stress and restore balance to the endocrine system, particularly the adrenal glands.",
      "Different adaptogens have unique properties—ashwagandha tends to be calming, rhodiola more energizing, and maca supportive of libido and vitality.",
      "These herbs work best when used consistently over time and matched to your specific needs and constitution."
    ],
    actionStep: {
      title: "Adaptogen Research",
      description: "Research one adaptogenic herb that interests you for your specific hormone challenges. Consider consulting with a healthcare provider about whether it might be appropriate for you."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Which adaptogen interests you most for your specific needs?",
      options: [
        "Ashwagandha (calming, thyroid support)",
        "Rhodiola (energy, focus, mood)",
        "Maca (libido, vitality, menopause)",
        "Holy basil/Tulsi (stress, mood, inflammation)",
        "Reishi (immune, sleep, liver support)"
      ]
    },
    quote: "Nature itself is the best physician.",
    quoteAuthor: "Hippocrates",
    points: 10
  },
  {
    day: 23,
    title: "The Birth Control Talk",
    subtitle: "Understanding Hormonal Contraception",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Hormonal birth control works by providing synthetic hormones that prevent ovulation and alter the uterine lining and cervical mucus.",
      "While offering reliable contraception and benefits for some conditions, hormonal contraceptives can mask underlying hormone imbalances rather than addressing root causes.",
      "If transitioning off hormonal birth control, understanding cycle charting, supporting nutrient repletion, and balancing hormones naturally becomes important."
    ],
    actionStep: {
      title: "Contraception Reflection",
      description: "Reflect on your current contraceptive method and whether it aligns with your health goals. Consider researching fertility awareness methods if interested in non-hormonal options."
    },
    checkInQuestion: {
      type: "text",
      question: "What questions do you have about hormonal birth control and its effects on your body?",
    },
    points: 10
  },
  {
    day: 24,
    title: "Hormone Testing: Understanding Your Options",
    subtitle: "Making Sense of Hormone Labs",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Various hormone testing methods exist, including serum (blood), saliva, and dried urine testing, each with different strengths and limitations.",
      "Single-point testing offers a snapshot, while cycle mapping provides insight into how your hormones change throughout your cycle.",
      "Working with a knowledgeable healthcare provider to interpret results in the context of your symptoms is crucial for meaningful insights."
    ],
    actionStep: {
      title: "Symptom Tracking",
      description: "Start or continue tracking your symptoms throughout your cycle to correlate with any hormone testing you might pursue. Note patterns in energy, mood, digestion, and other symptoms."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Have you had hormone testing done before?",
      options: [
        "Yes, comprehensive hormone panel",
        "Yes, basic hormone levels",
        "Yes, thyroid testing only",
        "No, but I'm interested",
        "No, and I'm not currently interested"
      ]
    },
    points: 10
  },
  {
    day: 25,
    title: "Inflammation and Hormones: The Connection",
    subtitle: "Reducing Inflammation for Hormone Balance",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Chronic inflammation disrupts hormone signaling, increases cortisol, and can exacerbate conditions like endometriosis, PCOS, and thyroid disorders.",
      "Inflammatory foods, gut imbalances, chronic stress, poor sleep, and environmental toxins all contribute to systemic inflammation.",
      "An anti-inflammatory lifestyle approach addresses multiple root causes of hormone imbalance simultaneously."
    ],
    actionStep: {
      title: "Anti-Inflammatory Meal",
      description: "Prepare an anti-inflammatory meal that includes fatty fish or plant-based omega-3s, colorful vegetables, herbs and spices like turmeric or ginger, and avoids refined oils and sugar."
    },
    checkInQuestion: {
      type: "mood",
      question: "How would you rate your overall physical comfort today?",
    },
    points: 10
  },
  {
    day: 26,
    title: "Optimizing Libido and Sexual Health",
    subtitle: "Hormonal and Emotional Factors in Desire",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Libido is influenced by a complex interplay of hormones (particularly testosterone, estrogen, and oxytocin), emotional well-being, relationship dynamics, and stress levels.",
      "Different phases of your cycle naturally impact desire, with many women experiencing increased libido around ovulation.",
      "Addressing hormone imbalances while also nurturing emotional intimacy and reducing stress can support a fulfilling experience of sexuality."
    ],
    actionStep: {
      title: "Pleasure Inventory",
      description: "Take time to reflect on what brings you pleasure in all areas of life, not just sexually. Increasing your capacity for joy and pleasure broadly can support sexual well-being."
    },
    checkInQuestion: {
      type: "text",
      question: "How do you notice your menstrual cycle affecting your energy and desire levels?",
    },
    points: 10
  },
  {
    day: 27,
    title: "Supporting Thyroid Health Naturally",
    subtitle: "Nutrition and Lifestyle for Thyroid Balance",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your thyroid requires specific nutrients including iodine, selenium, zinc, iron, and B vitamins to produce thyroid hormones properly.",
      "Common thyroid disruptors include certain foods (in sensitive individuals), environmental toxins, chronic stress, and nutrient deficiencies.",
      "Supporting your thyroid involves both adding supportive elements and removing potential interference with its function."
    ],
    actionStep: {
      title: "Thyroid-Supportive Breakfast",
      description: "Include two thyroid-supportive foods in your breakfast today, such as Brazil nuts for selenium, pumpkin seeds for zinc, or eggs for iodine and protein."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "Which thyroid symptom is most relevant to your experience?",
      options: [
        "Fatigue",
        "Cold intolerance",
        "Weight challenges",
        "Hair thinning",
        "Mood changes",
        "None - my thyroid feels balanced"
      ]
    },
    points: 10
  },
  {
    day: 28,
    title: "The Mind-Body Connection in Hormone Health",
    subtitle: "How Thoughts and Emotions Affect Your Hormones",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Your thoughts and emotions directly influence your hormonal state through the HPA (hypothalamic-pituitary-adrenal) axis and nervous system signals.",
      "Chronic stress, unprocessed emotions, and negative thought patterns can contribute to hormone imbalances over time.",
      "Mindfulness practices, cognitive reframing, and emotional release techniques can be powerful tools for supporting hormone balance."
    ],
    actionStep: {
      title: "Emotional Inventory",
      description: "Take 10 minutes to journal about any emotions you've been holding onto that might be affecting your physical well-being. Consider what support you need to process these feelings."
    },
    checkInQuestion: {
      type: "mood",
      question: "How would you rate your emotional well-being today?",
    },
    quote: "The body says what words cannot.",
    quoteAuthor: "Martha Graham",
    points: 10
  },
  {
    day: 29,
    title: "Creating Your Hormone Harmony Plan",
    subtitle: "Designing Your Personalized Approach",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "The most effective hormone balance plan is personalized to your specific symptoms, root causes, preferences, and lifestyle.",
      "Rather than trying to implement everything at once, focus on the foundations first: blood sugar balance, stress management, sleep, and liver/gut support.",
      "Small, consistent changes often create more sustainable hormone balance than dramatic overhauls that are difficult to maintain."
    ],
    actionStep: {
      title: "Priority Identification",
      description: "Review the topics we've covered and identify the THREE areas that feel most relevant to your hormone health journey right now. These will form the foundation of your personalized plan."
    },
    checkInQuestion: {
      type: "text",
      question: "What has been your most valuable insight from this hormone harmony journey?",
    },
    points: 15
  },
  {
    day: 30,
    title: "Celebrations and Continuation",
    subtitle: "Honoring Your Hormone Health Journey",
    videoId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    content: [
      "Congratulations on completing this 30-day hormone harmony journey! The awareness and practices you've cultivated can serve you for a lifetime.",
      "Remember that hormone balance is an ongoing practice, not a destination—your needs will continue to evolve throughout different life stages.",
      "Celebrating your progress, however small it may seem, reinforces your commitment to honoring your body's wisdom and needs."
    ],
    actionStep: {
      title: "Celebration Ritual",
      description: "Create a small ritual to celebrate completing this program. This might be treating yourself to something special, sharing your learnings with a friend, or simply taking a moment to acknowledge your commitment."
    },
    checkInQuestion: {
      type: "multiple-choice",
      question: "How would you like to continue your hormone health journey?",
      options: [
        "Deepening my knowledge about specific hormone topics",
        "Working with a hormone health practitioner",
        "Focusing on implementing what I've learned",
        "Sharing this knowledge with others",
        "Taking a break to integrate before continuing"
      ]
    },
    quote: "The journey of a thousand miles begins with a single step.",
    quoteAuthor: "Lao Tzu",
    points: 20
  }
];

// Function to get lesson by day number
export const getLessonByDay = (day: number): Lesson | undefined => {
  return lessons.find(lesson => lesson.day === day);
};

// Function to get a default lesson for preview purposes
export const getDefaultLesson = (): Lesson => {
  return lessons[0];
};

// Function to get all lessons
export const getLessons = (): Lesson[] => {
  return lessons;
};
